"use client";

import { RefObject, useEffect, useRef, useState, VideoHTMLAttributes } from "react";
import type HlsType from "hls.js";

import { logVideoPlayError } from "~/utils/videoPlayback";

type Props = VideoHTMLAttributes<HTMLVideoElement> & {
    ref?: RefObject<HTMLVideoElement | null>;
    lazy?: boolean;
    mp4: string;
    mp4Codec?: string;
    hls?: string;
    webm?: string;
    // Opt-in: stream the HLS source via hls.js on browsers without native HLS (Chrome/Firefox)
    // instead of downloading the mp4. hls.js is dynamically imported only when actually needed,
    // so it never touches the initial bundle or the page-load path.
    preferHls?: boolean;
};

// Native HLS playback (Safari/iOS) — resolved once on the client.
let nativeHlsSupport: boolean | null = null;
const supportsNativeHls = () => {
    if (nativeHlsSupport !== null) return nativeHlsSupport;
    if (typeof document === "undefined") return false;
    nativeHlsSupport = !!document.createElement("video").canPlayType("application/vnd.apple.mpegurl");
    return nativeHlsSupport;
};

export const VideoPlayer = ({ ref, lazy = true, mp4, mp4Codec = "video/mp4", hls, webm, preferHls = false, ...props }: Props) => {
    const internalRef = useRef<HTMLVideoElement>(null);
    const [isVisible, setVisible] = useState(!lazy);

    // When true, hls.js (or a direct mp4 fallback it triggers) drives playback, so we render no
    // <source> children — this keeps the browser from pre-downloading the mp4 on non-Safari.
    const streamViaHlsJs = isVisible && preferHls && !!hls && !supportsNativeHls();

    useEffect(() => {
        if (!internalRef.current || isVisible) return;

        const video = internalRef.current;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) return;
                setVisible(true);
                observer.disconnect();

                const video = entry.target as HTMLVideoElement;
                if (!video.autoplay) return;

                setTimeout(() => {
                    video.play().catch(logVideoPlayError);
                });
            });
        }, {
            rootMargin: "48px",
            threshold: [0.1, 0.2],
        });

        observer.observe(video);
        return () => observer.disconnect();
    }, [internalRef, isVisible]);

    useEffect(() => {
        const video = internalRef.current;
        if (!video || !streamViaHlsJs || !hls) return;

        let destroyed = false;
        let instance: HlsType | null = null;

        const playToMp4 = () => {
            video.src = mp4;
            video.load();
            if (video.autoplay) video.play().catch(logVideoPlayError);
        };

        import("hls.js").then(({ default: Hls }) => {
            if (destroyed || !internalRef.current) return;

            if (!Hls.isSupported()) {
                playToMp4();
                return;
            }

            const media = internalRef.current;
            instance = new Hls({ maxBufferLength: 15 });
            instance.loadSource(hls);
            instance.attachMedia(media);
            instance.on(Hls.Events.MANIFEST_PARSED, () => {
                if (media.autoplay) media.play().catch(logVideoPlayError);
            });
            instance.on(Hls.Events.ERROR, (_event, data) => {
                if (!data.fatal) return;
                instance?.destroy();
                instance = null;
                playToMp4();
            });
        }).catch(playToMp4);

        return () => {
            destroyed = true;
            instance?.destroy();
        };
    }, [streamViaHlsJs, hls, mp4]);

    // <source> children don't trigger a reload when their src changes — force the
    // browser to pick up new URLs (e.g. after an async feature-flag swap).
    useEffect(() => {
        const video = internalRef.current;
        if (!video || !isVisible || streamViaHlsJs) return;
        video.load();
    }, [mp4, hls, webm, isVisible, streamViaHlsJs]);

    const setRefs = (el: HTMLVideoElement | null) => {
        internalRef.current = el;
        if (ref) ref.current = el;
    }

    return (
        <video ref={setRefs} {...props}>
            {isVisible && !streamViaHlsJs && hls && <source src={hls} type="application/x-mpegURL" />}
            {isVisible && !streamViaHlsJs && <source src={mp4} type={mp4Codec} />}
            {isVisible && !streamViaHlsJs && webm && <source src={webm} type="video/webm" />}
        </video>
    );
};

export default VideoPlayer;
